import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import './LanguageSwitcher.css';

const languages = [
  { code: 'en', name: 'English', country: 'GB' },
  { code: 'fr', name: 'Français', country: 'FR' },
  { code: 'ar', name: 'العربية', country: 'EG' },
  { code: 'es', name: 'Español', country: 'ES' },
  { code: 'de', name: 'Deutsch', country: 'DE' }
];

export default function LanguageSwitcher({ theme }) {
  const { i18n } = useTranslation();

  useEffect(() => {
    document.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
  }, [i18n.language]);

  const changeLanguage = (selectedOption) => {
    const code = selectedOption.value;
    i18n.changeLanguage(code);
    document.dir = code === 'ar' ? 'rtl' : 'ltr';
  };

  // Create options with simple labels - JSX will be handled by formatOptionLabel
  const options = languages.map(lang => ({
    value: lang.code,
    label: lang.name,
    country: lang.country,
    name: lang.name
  }));

  // Custom option formatting function
  const formatOptionLabel = (option) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <img 
        src={`https://cdn.jsdelivr.net/gh/umidbekk/react-flag-kit@1/assets/${option.country}.svg`}
        alt={option.country}
        width={20}
        height={20}
        style={{ display: 'block' }}
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
      <span>{option.name}</span>
    </div>
  );

  return (
    <div className="language-switcher">
      <Select
        value={options.find(opt => opt.value === i18n.language)}
        onChange={changeLanguage}
        options={options}
        formatOptionLabel={formatOptionLabel}
        isSearchable={false}
        classNamePrefix="react-select"
        menuPlacement="auto"
        menuPortalTarget={document.body}   // 👈 important
        styles={{
          control: (base, state) => ({
            ...base,
            backgroundColor: theme.body,
            color: theme.text,
            border: `1px solid ${theme.text}`,
            borderRadius: '5px',
            minHeight: '40px',
            minWidth: '120px',
            cursor: 'pointer',
            boxShadow: state.isFocused ? `0 0 0 1px ${theme.text}` : 'none',
            '&:hover': {
              borderColor: theme.text,
            }
          }),
          singleValue: (base) => ({
            ...base,
            color: theme.text,
          }),
          menu: (base) => ({
            ...base,
            backgroundColor: theme.body,
            border: `1px solid ${theme.text}`,
            borderRadius: '5px',
            zIndex: 1000,
          }),
          option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected 
              ? theme.highlight 
              : state.isFocused 
                ? theme.highlight + '20' 
                : theme.body,
            color: theme.text,
            cursor: 'pointer',
            pointerEvents: 'auto',
            '&:hover': {
              backgroundColor: theme.highlight + '40',
            }
          }),
          dropdownIndicator: (base) => ({
            ...base,
            color: theme.text,
            '&:hover': {
              color: theme.text,
            }
          }),
          indicatorSeparator: () => ({
            display: 'none',
          }),
        }}
      />
    </div>
  );
}
